import React, { useState } from "react";
import { CmsContent, Icon } from "components";
import "./Accordion.scss";
import classNames from "classnames";
import { Helmet } from "react-helmet";

const AccordionItem = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false);

  return content ? (
    <div
      className={classNames({
        accordion__item: true,
        "accordion__item--open": isOpen,
      })}
    >
      <div className="accordion__item__header" onClick={() => setIsOpen((previousState) => !previousState)}>
        <h3 className="accordion__item__title">{content.title}</h3>
        <div className="accordion__item__button">
          <Icon name="ArrowDown" color="--brand-secondary" size={1.25} />
        </div>
      </div>
      <div className="accordion__item__content">
        {content.content ? <CmsContent content={content.content} /> : null}
      </div>
    </div>
  ) : null;
};

interface AccordionProps {
  rows: {
    title: string;
    content: string;
  }[];
  schema: boolean;
  intro: string;
}

/**
 * Returns an accordion component
 *
 * @param rows - An object array of each accordion item
 * @param schema - Boolean for whether to show FAQ schema
 * @param intro - An object of content
 * @returns A standard accordion component
 *
 */
export const Accordion = ({ rows, schema, intro }: AccordionProps): JSX.Element => {
  // maps through the content and returns it in the format of the schema
  const schemaContent = rows.map((faq) => {
    return {
      "@type": "Question",
      name: faq.title,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.content,
      },
    };
  });

  const accordionSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [schemaContent],
  };

  return (
    <div className="accordion">
      {schema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(accordionSchema)}</script>
        </Helmet>
      )}
      {intro && (
        <div className="accordion__intro">
          <CmsContent content={intro} />
        </div>
      )}
      {Object.keys(rows).length > 0 &&
        rows?.map((item, index) => {
          return <AccordionItem content={item} key={`accordionItem_${index}`} />;
        })}
    </div>
  );
};
